import React from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import {
	createTheme,
	ThemeProvider,
	extendTheme as materialExtendTheme,
	CssVarsProvider as MaterialCssVarsProvider,
	THEME_ID as MATERIAL_THEME_ID,
} from '@mui/material/styles'
import { koKR } from '@mui/x-data-grid/locales'
import { koKR as pickersKoKR } from '@mui/x-date-pickers/locales'
import { koKR as coreKoKR } from '@mui/material/locale'

import { View } from '@components/atoms'
import { AppWrapper } from '@components/systems'

import { useAppSelector, ConfigStateType } from '@redux/index'
import RouteProvider from '@routes/RouteProvider'
import { blue, deepPurple, pink } from '@mui/material/colors'
import RouteChangeTracker from './RouteChangeTracker'

import { CssVarsProvider as JoyCssVarsProvider } from '@mui/joy/styles'
import CssBaseline from '@mui/material/CssBaseline'
import 'react-international-phone/style.css'
import './App.css'

const App = () => {
	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const backgroundColor: string = useAppSelector(state => state.config.backgroundColor)
	const mainBackgroundColor: string = useAppSelector(
		state => state.config.mainBackgroundColor,
	)
	const isLogin: boolean = useAppSelector(state => state.auth.isLogin as boolean)
	const userLanguage: string = useAppSelector(state => state.user.language)

	RouteChangeTracker()

	React.useEffect(() => {
		const newBackgroundColor: string = isLogin ? mainBackgroundColor : backgroundColor

		document.body.style.backgroundColor = newBackgroundColor
	}, [isLogin, backgroundColor, mainBackgroundColor])

	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				retry: 0,
				suspense: true,
			},
		},
	})

	const primaryColor = mode === 'admin' ? blue : deepPurple

	const theme = React.useMemo(() => {
		const initTheme = {
			palette: {
				primary: {
					...primaryColor,
					main: mode === 'admin' ? blue['A400'] : deepPurple['A400'],
				},
				error: {
					...pink,
					main: pink['A400'],
				},
			},
		}

		const res =
			userLanguage === 'ko'
				? createTheme(initTheme, koKR, pickersKoKR, coreKoKR)
				: createTheme(initTheme)

		return res
	}, [userLanguage, mode])

	const materialTheme = materialExtendTheme({
		colorSchemes: {
			light: {
				palette: {
					primary: {
						main: mode === 'admin' ? blue['A400'] : deepPurple['A400'],
					},
				},
			},
		},
	})

	return (
		<MaterialCssVarsProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
			<CssBaseline enableColorScheme />
			<QueryClientProvider client={queryClient}>
				<ThemeProvider theme={theme}>
					<JoyCssVarsProvider>
						<AppWrapper>
							<View className="App">
								<RouteProvider />
							</View>
						</AppWrapper>
					</JoyCssVarsProvider>
				</ThemeProvider>
			</QueryClientProvider>
		</MaterialCssVarsProvider>
	)
}

export default App
